import { useState } from "react";
import { useAtomValue } from "jotai";
import { useOutletContext } from "react-router-dom";
import { apiCall } from "../../lib/api-client.js";
import {
  ApiError,
  type LeadershipRow,
  type PublicRole,
  type PublicUser,
  type UpsertLeadershipBody,
  type UsersListResponse,
} from "../../lib/api-types.js";
import { authAtom } from "../../lib/auth-store.js";
import { useApiQuery } from "../../lib/useApiQuery.js";
import {
  leadershipByRolePath,
  leadershipPath,
  rolesPath,
  usersPath,
} from "../../lib/api-paths.js";
import { ErrorBanner } from "../../components/ErrorBanner.js";
import { Empty } from "../../components/Empty.js";
import { Modal } from "../../components/Modal.js";
import { Table, type ColumnDef } from "../../components/Table.js";
import {
  FormField,
  PrimaryButton,
  SecondaryButton,
  TextInput,
} from "../../components/FormField.js";
import type { OrgDetailContext } from "./context.js";

interface RoleRow {
  role: PublicRole;
  leaderUserIds: string[];
}

export function LeadershipPanel(): JSX.Element {
  const { org } = useOutletContext<OrgDetailContext>();
  const auth = useAtomValue(authAtom);
  const [editing, setEditing] = useState<RoleRow | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const rolesQ = useApiQuery<PublicRole[]>(rolesPath(org.code), [org.code]);
  const leadershipQ = useApiQuery<LeadershipRow[]>(
    leadershipPath(org.code),
    [org.code],
  );

  const rows: RoleRow[] =
    rolesQ.data?.map((role) => ({
      role,
      leaderUserIds:
        leadershipQ.data?.find((row) => row.roleCode === role.code)
          ?.leaderUserIds ?? [],
    })) ?? [];

  const loading = rolesQ.loading || leadershipQ.loading;
  const error = rolesQ.error ?? leadershipQ.error;

  const handleUpsert = async (
    row: RoleRow,
    body: UpsertLeadershipBody,
  ): Promise<void> => {
    if (!auth) return;
    try {
      await apiCall("PUT", leadershipByRolePath(org.code, row.role.code), {
        body,
        token: auth.token,
      });
      setActionError(null);
      setEditing(null);
      await leadershipQ.refetch();
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : "保存失败");
    }
  };

  const handleClear = async (row: RoleRow): Promise<void> => {
    if (!auth) return;
    try {
      await apiCall("DELETE", leadershipByRolePath(org.code, row.role.code), {
        token: auth.token,
      });
      await leadershipQ.refetch();
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : "清空失败");
    }
  };

  const columns: ColumnDef<RoleRow>[] = [
    {
      header: "角色",
      render: (r) => (
        <span className="font-mono">{r.role.code}</span>
      ),
    },
    {
      header: "名称",
      render: (r) => r.role.name,
    },
    {
      header: "审批人",
      render: (r) =>
        r.leaderUserIds.length === 0 ? (
          <span className="text-dosia-text-dim text-xs">未指派</span>
        ) : (
          <div className="flex flex-wrap gap-1">
            {r.leaderUserIds.map((id) => (
              <span
                key={id}
                className="inline-flex items-center rounded-full bg-dosia-accent/15 text-dosia-text px-2 py-0.5 text-xs font-mono"
              >
                {id.slice(-6)}
              </span>
            ))}
          </div>
        ),
    },
    {
      header: "操作",
      width: "w-36",
      render: (r) => (
        <div className="flex gap-2">
          <button
            type="button"
            className="text-xs text-dosia-accent hover:underline"
            onClick={() => setEditing(r)}
          >
            编辑
          </button>
          {r.leaderUserIds.length > 0 ? (
            <button
              type="button"
              className="text-xs text-amber-700 hover:underline"
              onClick={() => void handleClear(r)}
            >
              清空
            </button>
          ) : null}
        </div>
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-3">
      <p className="text-sm text-dosia-text-secondary">
        审批人指派决定 Skill 提交时哪些员工有权审批该角色员工的请求。每个角色可指派
        多个审批人。
      </p>
      {error ? <ErrorBanner>{error.message}</ErrorBanner> : null}
      {actionError ? (
        <ErrorBanner onDismiss={() => setActionError(null)}>
          {actionError}
        </ErrorBanner>
      ) : null}
      <Table<RoleRow>
        columns={columns}
        rows={rows}
        rowKey={(r) => r.role.id}
        loading={loading}
        empty={<Empty title="尚无角色" />}
      />

      {editing ? (
        <LeaderPickerDialog
          row={editing}
          orgCode={org.code}
          onCancel={() => setEditing(null)}
          onSubmit={(body) => handleUpsert(editing, body)}
        />
      ) : null}
    </div>
  );
}

function LeaderPickerDialog({
  row,
  orgCode,
  onCancel,
  onSubmit,
}: {
  row: RoleRow;
  orgCode: string;
  onCancel: () => void;
  onSubmit: (body: UpsertLeadershipBody) => Promise<void>;
}): JSX.Element {
  const [selected, setSelected] = useState<string[]>(row.leaderUserIds);
  const [q, setQ] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const qs = new URLSearchParams({ pageSize: "200", status: "active" });
  if (q.trim()) qs.set("q", q.trim());
  const { data, loading } = useApiQuery<UsersListResponse>(
    `${usersPath(orgCode)}?${qs.toString()}`,
    [orgCode, q],
  );

  const toggle = (id: string): void => {
    setSelected((curr) =>
      curr.includes(id) ? curr.filter((x) => x !== id) : [...curr, id],
    );
  };

  return (
    <Modal
      open
      onClose={onCancel}
      title={`指派审批人 - ${row.role.code}`}
      size="lg"
      footer={
        <>
          <SecondaryButton onClick={onCancel}>取消</SecondaryButton>
          <PrimaryButton
            disabled={submitting}
            onClick={async () => {
              setSubmitting(true);
              await onSubmit({ leaderUserIds: selected });
              setSubmitting(false);
            }}
          >
            保存（已选 {selected.length}）
          </PrimaryButton>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <FormField label="搜索用户" hint="按 user 名 / 飞书姓名">
          <TextInput value={q} onChange={(e) => setQ(e.target.value)} />
        </FormField>
        {loading ? (
          <p className="text-sm text-dosia-text-dim">加载中...</p>
        ) : (
          <ul className="max-h-[320px] overflow-auto border border-dosia-border rounded-md divide-y divide-dosia-border-light">
            {(data?.rows ?? []).map((u: PublicUser) => (
              <li
                key={u.id}
                className="flex items-center gap-2 px-3 py-2 hover:bg-dosia-bg-lighter"
              >
                <input
                  type="checkbox"
                  checked={selected.includes(u.id)}
                  onChange={() => toggle(u.id)}
                  aria-label={`选择 ${u.user}`}
                />
                <span className="font-mono text-sm">{u.user}</span>
                {u.feishu?.name ? (
                  <span className="text-xs text-dosia-text-secondary">
                    （{u.feishu.name}）
                  </span>
                ) : null}
                {u.role ? (
                  <span className="ml-auto text-xs text-dosia-text-dim">
                    {u.role}
                  </span>
                ) : null}
              </li>
            ))}
            {(data?.rows ?? []).length === 0 ? (
              <li className="px-3 py-4 text-center text-sm text-dosia-text-dim">
                无匹配用户
              </li>
            ) : null}
          </ul>
        )}
      </div>
    </Modal>
  );
}
